import { ChangeEvent, FC, FormEvent } from "react";
import styles from "./styles.module.css";
import { LocalizedText, Accomplishment } from "../../services/generated/client";
import { FileUploader } from "../FileUploader";

interface FormField {
  id: string;
  label: string;
  type: string;
  value?: string;
  onChange?: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => void;

  values?: string[];
  onImageChange?: (index: number, value: string) => void;
  onAddImage?: () => void;
  onRemoveImage?: (index: number) => void;

  accomplishments?: Accomplishment[];
  onAccomplishmentChange?: (
    index: number,
    field: keyof Accomplishment,
    value: LocalizedText | string
  ) => void;
  onAddAccomplishment?: () => void;
  onRemoveAccomplishment?: (index: number) => void;

  options?: { label: string; value: string }[];
}

interface FormProps {
  title: string;
  fields: FormField[];
  submitLabel: string;
  onSubmit: (e: FormEvent) => void;
  isView: boolean;
  languageEditing: "en" | "ka";
  isAdding: boolean;
}

const PopUpForm: FC<FormProps> = ({
  title,
  fields,
  submitLabel,
  onSubmit,
  isView,
  languageEditing,
  isAdding,
}) => {
  return (
    <div className={styles.wrapper}>
      <div className={styles.container}>
        <h2 className={styles.title}>
          {isView ? title : isAdding ? `Add ${title}` : `Edit ${title}`}
        </h2>
        <form onSubmit={onSubmit} className={styles.form}>
          {fields.map((field) => (
            <div className={styles.formFields} key={field.id}>
              <label className={styles.label} htmlFor={field.id}>
                {field.label}
              </label>
              {field.type === "imageUrls" ? (
                <div className={styles.dynamicArray}>
                  {field.values?.map((val, index) => (
                    <div key={val} className={styles.group}>
                      <label className={styles.label}>
                        {field.label} #{index + 1}
                      </label>

                      <FileUploader
                        value={val || ""}
                        onUploaded={(url: string) => {
                          if (url) {
                            field.onImageChange?.(index, url);
                          } else {
                            field.onRemoveImage?.(index);
                          }
                        }}
                        onDeleted={() => field.onRemoveImage?.(index)}
                        isView={isView}
                        path={title}
                      />
                    </div>
                  ))}
                  {!isView && (
                    <button
                      type="button"
                      className={styles.addBtn}
                      onClick={field.onAddImage}
                      disabled={field.values?.some((val) => !val)}
                    >
                      + Add Image
                    </button>
                  )}
                </div>
              ) : field.type === "imageUrl" ? (
                <div className={styles.group}>
                  <FileUploader
                    value={field.value}
                    onUploaded={(url: string) => {
                      if (url) {
                        field.onImageChange?.(0, url);
                      } else {
                        field.onRemoveImage?.(0);
                      }
                    }}
                    onDeleted={() => field.onImageChange?.(0, "")}
                    isView={isView}
                    path={title}
                  />
                </div>
              ) : field.type === "checkbox" ? (
                <input
                  type="checkbox"
                  id={field.id}
                  checked={field.value === "true"}
                  onChange={field.onChange}
                  disabled={isView}
                  className={styles.checkbox}
                />
              ) : field.type === "textarea" ? (
                <textarea
                  id={field.id}
                  className={styles.textarea}
                  value={field.value}
                  onChange={field.onChange}
                  disabled={isView}
                />
              ) : field.type === "accomplishments" ? (
                <div className={styles.dynamicArray}>
                  {field.accomplishments?.map((accomplishment, index) => (
                    <div key={index} className={styles.group}>
                      <input
                        className={styles.input}
                        type="text"
                        placeholder="Number"
                        value={accomplishment.number}
                        onChange={(e) =>
                          field.onAccomplishmentChange?.(
                            index,
                            "number",
                            e.target.value
                          )
                        }
                        disabled={isView}
                      />

                      <div className={styles.languageInputs}>
                        <input
                          className={styles.input}
                          type="text"
                          placeholder={`Header (${languageEditing.toUpperCase()})`}
                          value={accomplishment.header?.[languageEditing]}
                          onChange={(e) =>
                            field.onAccomplishmentChange?.(index, "header", {
                              ...accomplishment.header,
                              [languageEditing]: e.target.value,
                            })
                          }
                          disabled={isView}
                        />
                      </div>

                      <div className={styles.languageInputs}>
                        <textarea
                          className={styles.textarea}
                          placeholder={`Paragraph (${languageEditing.toUpperCase()})`}
                          value={accomplishment.paragraph?.[languageEditing]}
                          onChange={(e) =>
                            field.onAccomplishmentChange?.(index, "paragraph", {
                              ...accomplishment.paragraph,
                              [languageEditing]: e.target.value,
                            })
                          }
                          disabled={isView}
                        />
                      </div>

                      {!isView && (
                        <button
                          type="button"
                          className={styles.removeBtn}
                          onClick={() => field.onRemoveAccomplishment?.(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}

                  {!isView && (
                    <button
                      type="button"
                      className={styles.addBtn}
                      onClick={field.onAddAccomplishment}
                    >
                      + Add Accomplishment
                    </button>
                  )}
                </div>
              ) : (
                <input
                  id={field.id}
                  className={styles.input}
                  type={field.type}
                  value={field.value as string}
                  onChange={field.onChange}
                  disabled={isView}
                />
              )}
            </div>
          ))}
          {!isView && (
            <button className={styles.primaryButton} type="submit">
              {submitLabel}
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default PopUpForm;
